import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { addGeofenceCheckedId, addGeofenceCheckedItemsId, deleteGeofenceCheckedId, setGeofenceCheckedId } from 'app/actions/map';
import { Autosizer, Scrollbar } from 'app/components';
import {
  geofenceCheckedIdMapSelector,
  geofenceSearchValueMapSelector,
  refMapSelector,
  sortItemsFilterGeofenceListSelector,
  sortItemsFlatMapSelector,
} from 'app/selectors/map';
import React, { forwardRef, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FixedSizeList } from 'react-window';

const MapFilterGeofenceList = () => {
  const listRef = useRef(null);
  const dispatch = useDispatch();
  const mapRef = useSelector(refMapSelector);
  const geofenceSearchValue = useSelector(geofenceSearchValueMapSelector);
  const geofenceCheckedId = useSelector(geofenceCheckedIdMapSelector);
  const sortItems = useSelector(sortItemsFilterGeofenceListSelector);
  const sortItemsFlat = useSelector(sortItemsFlatMapSelector);
  const [expanded, setExpanded] = useState(-1);

  const handleAccordionChange = (key) => (event, expanded) => {
    setExpanded(expanded ? key : null);
  };

  const handleItemClick = (event, value) => {
    event.preventDefault();
    event.stopPropagation();

    if (geofenceCheckedId.indexOf(value) === -1) {
      dispatch(addGeofenceCheckedId(value.id));
    }

    mapRef.current.handleClosePopup();
    mapRef.current.handleFlyTo([value.latitude, value.longitude]);
  };

  const handleCheckboxItemClick = (event, id) => {
    event.preventDefault();
    event.stopPropagation();

    dispatch(geofenceCheckedId.indexOf(id) !== -1 ? deleteGeofenceCheckedId(id) : addGeofenceCheckedId(id));
  };

  const handleCheckboxSummaryClick = (event, key) => {
    event.preventDefault();
    event.stopPropagation();

    const filterItems = sortItems[key].geofences.map((i) => i.id).filter((id) => geofenceCheckedId.indexOf(id) === -1);
    const checkedItems = geofenceCheckedId.filter((id) => sortItems[key].geofences.map((i) => i.id).indexOf(id) === -1);

    dispatch(filterItems.length ? addGeofenceCheckedItemsId(filterItems) : setGeofenceCheckedId(checkedItems));
  };

  const filteredIds = useMemo(() => {
    if (geofenceSearchValue.length === 0) {
      return sortItems.flatMap((group) => group.geofences.map((g) => g.id));
    } else {
      return sortItemsFlat.map((item) => item.id);
    }
  }, [geofenceSearchValue, sortItems, sortItemsFlat]);

  const handleSelectAll = () => {
    const allSelected = filteredIds.length > 0 && filteredIds.every((id) => geofenceCheckedId.includes(id));

    if (allSelected) {
      const remainingChecked = geofenceCheckedId.filter((id) => !filteredIds.includes(id));
      dispatch(setGeofenceCheckedId(remainingChecked));
    } else {
      const newIds = filteredIds.filter((id) => !geofenceCheckedId.includes(id));
      dispatch(addGeofenceCheckedItemsId(newIds));
    }
  };

  const allSelected = filteredIds.length > 0 && filteredIds.every((id) => geofenceCheckedId.includes(id));
  const someSelected = filteredIds.some((id) => geofenceCheckedId.includes(id)) && !allSelected;

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        '.simplebar-offset > div': {
          '&::-webkit-scrollbar': {
            width: 0,
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
            boxShadow: 'none',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        },
      }}>
      <Box
        sx={(theme) => ({
          flexShrink: 0,
          padding: theme.spacing(1, 2),
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'background.default',
          },
        })}
        onClick={handleSelectAll}>
        <Checkbox
          size='small'
          color='primary'
          disableRipple
          checked={allSelected}
          indeterminate={someSelected}
          sx={{
            padding: 0,
            svg: {
              fontSize: '18px',
            },
          }}
        />
        <Typography sx={{ ml: 1 }}>Выделить всё</Typography>
      </Box>

      {geofenceSearchValue.length === 0 && (
        <Stack
          direction='column'
          sx={(theme) => ({
            flex: '1 1 auto',
            minHeight: 0,
            overflow: 'hidden',
            '.simplebar-content-wrapper': {
              padding: theme.spacing(0),
            },
            '.MuiAccordion-root': {
              borderRadius: '0',
              overflow: 'hidden',
              transition: 'background-color ' + theme.transitions.duration.shortest + 'ms ' + theme.transitions.easing.easeInOut + ' 0ms',
              '&:before': {
                display: 'none',
              },
              '&:last-of-type': {
                margin: '0!important',
              },
              '&.Mui-expanded': {
                margin: '0',
              },
              '&.Mui-disabled': {
                bgcolor: 'background.paper',
                '.MuiAccordionSummary-root': {
                  opacity: '1',
                },
                '.MuiAccordionSummary-expandIconWrapper': {
                  display: 'none',
                },
              },
              '.MuiAccordionSummary-expandIconWrapper': {
                svg: {
                  fontSize: '18px',
                },
              },
            },
            '.MuiAccordionSummary-root': {
              padding: theme.spacing(0, 2),
              minHeight: '24px',
              transition: 'none',
              '&:hover': {
                bgcolor: 'background.default',
                borderRadius: '0',
              },
              '&.Mui-expanded': {
                minHeight: '24px',
                bgcolor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                borderRadius: '0',
                '.MuiCheckbox-root': {
                  color: '#FFFFFF',
                  '&.Mui-checked': {
                    color: '#FFFFFF',
                  },
                },
                '.MuiAccordionSummary-expandIconWrapper': {
                  color: '#FFFFFF',
                },
              },
            },
            '.MuiAccordionSummary-content': {
              alignItems: 'center',
              overflow: 'hidden',
              margin: theme.spacing(0),
              '&.Mui-expanded': {
                margin: theme.spacing(0),
              },
              '.MuiCheckbox-root': {
                minWidth: '26px',
                justifyContent: 'flex-start',
                padding: theme.spacing(0),
                svg: {
                  fontSize: '18px',
                },
              },
            },
            '.MuiAccordionDetails-root': {
              padding: theme.spacing(0),
              '.MuiList-root': {
                margin: '2px 0',
              },
            },
          })}>
          <Scrollbar>
            {sortItems.map(({ id, name, geofences }, key) => {
              const checkedFilter = geofences.filter((i) => geofenceCheckedId.indexOf(i.id) !== -1);

              return (
                <Accordion
                  key={id}
                  square
                  elevation={0}
                  disabled={geofences.length === 0}
                  expanded={expanded === key}
                  onChange={handleAccordionChange(key)}
                  TransitionProps={{ unmountOnExit: true }}>
                  <AccordionSummary disableRipple expandIcon={<ExpandMore />}>
                    <Checkbox
                      size='small'
                      color='primary'
                      disableRipple
                      disabled={geofences.length === 0}
                      checked={geofences.length !== 0 && checkedFilter.length === geofences.length}
                      indeterminate={checkedFilter.length !== 0 && checkedFilter.length < geofences.length}
                      onClick={(event) => handleCheckboxSummaryClick(event, key)}></Checkbox>
                    <Typography>
                      {name} ({geofences.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List disablePadding>
                      {geofences.map((geofence, key) => (
                        <ListItem
                          key={key}
                          disablePadding
                          onClick={(event) => handleItemClick(event, geofence)}
                          sx={(theme) => ({
                            padding: theme.spacing(0.5, 1),
                            paddingLeft: '36px',
                            borderRadius: '0',
                            '&:hover': {
                              bgcolor: 'background.default',
                              cursor: 'pointer',
                            },
                            '.MuiListItemIcon-root': {
                              minWidth: '26px',
                              '.MuiCheckbox-root': {
                                padding: '0',
                                minWidth: '26px',
                                justifyContent: 'flex-start',
                                svg: {
                                  fontSize: '18px',
                                },
                              },
                            },
                            '.MuiListItemText-root': {
                              margin: theme.spacing(0),
                            },
                          })}>
                          <ListItemIcon>
                            <Checkbox
                              size='small'
                              color='primary'
                              disableRipple
                              checked={geofenceCheckedId.indexOf(geofence.id) !== -1}
                              onClick={(event) => handleCheckboxItemClick(event, geofence.id)}></Checkbox>
                          </ListItemIcon>
                          <ListItemText primary={geofence.name} />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Scrollbar>
        </Stack>
      )}
      {geofenceSearchValue.length !== 0 && (
        <Box sx={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}>
          <Autosizer>
            {({ height, width }) => (
              <Box sx={{ height, width }}>
                <Scrollbar>
                  {({ scrollableNodeRef, contentNodeRef }) => {
                    return (
                      <FixedSizeList
                        ref={listRef}
                        height={height}
                        itemSize={24}
                        overscanCount={10}
                        innerRef={contentNodeRef}
                        outerRef={scrollableNodeRef}
                        innerElementType={MapFilterGeofenceListInnerElementType}
                        itemCount={sortItemsFlat.length}
                        itemData={{
                          items: sortItemsFlat,
                          checkedId: geofenceCheckedId,
                          onCheckboxClick: handleCheckboxItemClick,
                          onClick: handleItemClick,
                        }}>
                        {MapFilterGeofenceListItem}
                      </FixedSizeList>
                    );
                  }}
                </Scrollbar>
              </Box>
            )}
          </Autosizer>
        </Box>
      )}
    </Box>
  );
};

const MapFilterGeofenceListInnerElementType = forwardRef((props, ref) => <List ref={ref} disablePadding {...props} />);

const MapFilterGeofenceListItem = ({ data, index, style }) => {
  const { items, onClick, checkedId, onCheckboxClick } = data;

  return (
    <ListItem
      style={style}
      disablePadding
      onClick={(event) => onClick(event, items[index])}
      sx={(theme) => ({
        padding: theme.spacing(0.5, 1),
        paddingLeft: '12px',
        borderRadius: '0',
        '&:hover': {
          bgcolor: 'background.default',
          cursor: 'pointer',
        },
        '.MuiListItemIcon-root': {
          minWidth: '26px',
          '.MuiCheckbox-root': {
            padding: '0',
            minWidth: '26px',
            justifyContent: 'flex-start',
            svg: {
              fontSize: '18px',
            },
          },
        },
        '.MuiListItemText-root': {
          margin: theme.spacing(0),
        },
      })}>
      <ListItemIcon>
        <Checkbox
          size='small'
          color='primary'
          disableRipple
          checked={checkedId.indexOf(items[index].id) !== -1}
          onClick={(event) => onCheckboxClick(event, items[index].id)}></Checkbox>
      </ListItemIcon>
      <ListItemText primary={items[index].name} />
    </ListItem>
  );
};

export default MapFilterGeofenceList;
